import React from 'react';
import { Check } from 'lucide-react';

import { cn, EYEBROW, FOCUS_RING_ITEM } from '../lib/utils';

export interface StepDescriptor {
  /** Stable key, also used as the deep-link fragment. */
  id: string;
  /** Short label shown beside or under the marker. */
  label: string;
}

interface StepIndicatorProps {
  steps: StepDescriptor[];
  /** Zero-based index of the step being shown. */
  currentIndex: number;
  /** Ids of steps proven complete by server state. */
  completedIds: string[];
  /** Jump to a step. Omit to render a non-interactive indicator. */
  onSelect?: (index: number) => void;
  /**
   * `horizontal` is a compact strip for narrow hosts; `vertical` is a rail
   * with every label always legible, which is what a flow of more than about
   * five steps needs — a horizontal strip wraps to two rows and stops reading
   * as a sequence.
   */
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

/**
 * Progress across a multi-step flow.
 *
 * Hand-built rather than pulled from Radix: `@radix-ui/react-progress` is not
 * among the portal's installed primitives, and adding a dependency for one
 * track-and-fill is not worth the bundle re-check. The aria contract is
 * therefore written out explicitly here.
 *
 * Completion is a fact about server state, not about where the user has
 * clicked, so a step can be complete while the user stands somewhere earlier.
 */
export const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentIndex,
  completedIds,
  onSelect,
  orientation = 'horizontal',
  className,
}) => {
  const completed = new Set(completedIds);
  const completedCount = steps.filter((step) => completed.has(step.id)).length;

  const progressAria = {
    role: 'progressbar' as const,
    'aria-valuemin': 0,
    'aria-valuemax': steps.length,
    'aria-valuenow': completedCount,
    'aria-valuetext': `${completedCount} of ${steps.length} steps complete`,
  };

  /**
   * Marker + label, shared by both orientations.
   *
   * `withConnector` draws the rail's vertical line down to the next marker.
   * It hangs off the marker itself — `left-1/2 -translate-x-1/2` — rather than
   * being positioned against the row, so it stays centred on the dot no matter
   * what horizontal padding the row carries. The previous version hardcoded an
   * offset against the row and sat 3.5px to the right of the dots.
   *
   * Its height is the vertical gap between two markers: the row's `py-1.5`
   * twice, plus the list's `gap-0.5` — 6 + 6 + 2 = 14px.
   */
  const renderMarker = (
    step: StepDescriptor,
    index: number,
    isDone: boolean,
    isCurrent: boolean,
    withConnector = false,
  ) => (
    <>
      <span
        className={cn(
          'relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-2xs font-semibold tabular-nums transition-colors',
          isDone && 'bg-success-emphasis text-background',
          !isDone && isCurrent && 'bg-primary text-primary-foreground',
          !isDone && !isCurrent && 'bg-muted text-muted-foreground',
        )}
      >
        {isDone ? <Check aria-hidden="true" className="h-3.5 w-3.5" /> : index + 1}
        {withConnector ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute left-1/2 top-full h-3.5 w-px -translate-x-1/2',
              isDone ? 'bg-success-emphasis' : 'bg-border',
            )}
          />
        ) : null}
      </span>
      <span
        className={cn(
          'text-xs',
          // sr-only, never `hidden`: below `sm` the strip shows markers only,
          // and a hidden label would leave each button named by a bare digit.
          orientation === 'horizontal' && 'sr-only sm:not-sr-only',
          isCurrent ? 'font-medium text-foreground' : 'text-muted-foreground',
        )}
      >
        {step.label}
      </span>
      {/* The check icon is decorative; completion is said in words. */}
      {isDone ? <span className="sr-only">, completed</span> : null}
    </>
  );

  if (orientation === 'vertical') {
    return (
      <nav aria-label="Setup steps" className={cn('flex flex-col gap-3', className)}>
        <div>
          <p className={EYEBROW}>Progress</p>
          <p {...progressAria} className="mt-1 text-sm font-medium tabular-nums text-foreground">
            {completedCount} of {steps.length}
          </p>
        </div>

        <ol className="relative flex flex-col gap-0.5">
          {steps.map((step, index) => {
            const isDone = completed.has(step.id);
            const isCurrent = index === currentIndex;
            const content = renderMarker(step, index, isDone, isCurrent, index < steps.length - 1);

            return (
              <li key={step.id} className="relative">
                {onSelect ? (
                  <button
                    type="button"
                    onClick={() => onSelect(index)}
                    aria-current={isCurrent ? 'step' : undefined}
                    className={cn(
                      'relative flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors',
                      FOCUS_RING_ITEM,
                      isCurrent ? 'bg-muted' : 'hover:bg-muted/60',
                    )}
                  >
                    {content}
                  </button>
                ) : (
                  <span
                    aria-current={isCurrent ? 'step' : undefined}
                    className={cn(
                      'relative flex w-full items-center gap-2.5 rounded-md px-2 py-1.5',
                      isCurrent && 'bg-muted',
                    )}
                  >
                    {content}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div {...progressAria} className="flex h-1 w-full gap-1 overflow-hidden rounded-full">
        {steps.map((step) => (
          <span
            key={step.id}
            className={cn(
              'h-full flex-1 rounded-full transition-colors',
              completed.has(step.id) ? 'bg-success-emphasis' : 'bg-muted',
            )}
          />
        ))}
      </div>

      <ol className="flex flex-wrap items-center gap-x-1 gap-y-1.5">
        {steps.map((step, index) => {
          const isDone = completed.has(step.id);
          const isCurrent = index === currentIndex;
          const marker = renderMarker(step, index, isDone, isCurrent);

          return (
            <li key={step.id} className="flex items-center gap-1">
              {onSelect ? (
                <button
                  type="button"
                  onClick={() => onSelect(index)}
                  aria-current={isCurrent ? 'step' : undefined}
                  className={cn('flex items-center gap-1.5 rounded px-1 py-0.5 transition-colors hover:bg-muted', FOCUS_RING_ITEM)}
                >
                  {marker}
                </button>
              ) : (
                <span
                  className="flex items-center gap-1.5 px-1 py-0.5"
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {marker}
                </span>
              )}
              {index < steps.length - 1 ? (
                <span aria-hidden="true" className="h-px w-2 bg-border sm:w-3" />
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
};
